// canvas/upcard.js: bid-phase up-card renderer (M2.c-3).
//
// During bid_r1 the up-card sits face-up at the center (trick area) at
// >=110px width per plan AC #3. After all r1 passes (up_card_turned_down
// = true) the card is replaced by a faded suit pip with a strikethrough
// at the kitty position so the player can see WHICH suit got rejected.
//
// During dealer_discard the up-card stays visible (it's the card the
// dealer is picking up). renderDealerPickup draws it with an amber
// outline + caption so the player understands the action.

import { renderCard, isTrumpCard, _internals as cardInternals } from './cards.js';
import { canvasFont } from './text.js';
import { trumpBadgeStackRect } from './trump-badge.js';
// T9: the upcard scale-in rides the synthesis §4.4 spring curve.
import { springOvershoot } from '../ui/easing.js';

const { SUIT_NAME, SUIT_SYMBOL, pipColor, suitOf } = cardInternals;

const UPCARD_W_DESKTOP = 110;
const UPCARD_W_MOBILE  = 90;
const UPCARD_ASPECT    = 3.5 / 2.5;

const COLOR_REJECTED = 'rgba(200, 200, 200, 0.4)';
const COLOR_STRIKE   = '#E03048';
const COLOR_PICKUP_GLOW = '#F0C040';   // amber, matches DiscardPrompt border
const COLOR_PICKUP_TEXT = '#F5F5F0';   // cream

const RANK_NAME = {
  '9': '9',
  'T': '10',
  'J': 'Jack',
  'Q': 'Queen',
  'K': 'King',
  'A': 'Ace',
};

function describeCardLong(cardId) {
  if (!cardId || cardId.length !== 2) return '';
  const rank = RANK_NAME[cardId[0]] || cardId[0];
  const suit = SUIT_NAME[cardId[1]] || '';
  return `${rank} of ${suit}`;
}

/**
 * Pick a human-readable label for the dealer. Prefer the occupant's
 * display_name; only fall back to "Bot N" when the snapshot says the
 * seat is a bot (or doesn't carry seat_occupants at all, which is the
 * single-player bot-fill case where the bot label is correct).
 *
 * @param {{
 *   dealer_seat?: number|null,
 *   seat_occupants?: Record<string|number, {display_name?: string, is_bot?: boolean}>|null,
 * }} state
 * @returns {string}
 */
function dealerLabelFor(state) {
  const seat = state.dealer_seat;
  if (seat == null) return 'Dealer';
  const occs = state.seat_occupants;
  if (occs) {
    const occ = occs[seat] || occs[String(seat)];
    if (occ?.display_name && !occ.is_bot) return occ.display_name;
  }
  return `Bot ${seat}`;
}

/**
 * Position rect for the up-card at trickArea center. Pure, exposed
 * so callers (M2.c-11 dealer-discard UI) can place a clickable
 * overlay button on top of the canvas-drawn card.
 *
 * B1-1: prefers the layout's own upCard rect (computed in layout.js
 * from this same box) so renderers, the bidding zone, and the trump
 * splash all agree on where the card is; falls back to computing it
 * for layouts that predate the field.
 *
 * @param {import('./layout.js').TableLayout} layout
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
export function computeUpCardRect(layout) {
  if (layout && layout.upCard) return { ...layout.upCard };
  const w = layout.isMobile ? UPCARD_W_MOBILE : UPCARD_W_DESKTOP;
  const h = Math.round(w * UPCARD_ASPECT);
  const cx = layout.trickArea.x + Math.round(layout.trickArea.w / 2);
  const cy = layout.trickArea.y + Math.round(layout.trickArea.h / 2);
  const x = cx - Math.round(w / 2);
  const y = cy - Math.round(h / 2);
  return { x, y, w, h };
}

/**
 * Render the up-card at trickArea center.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   current_state?: string,
 *   up_card?: string|null,
 *   up_card_turned_down?: boolean,
 *   trump_suit?: string|null,
 * }} state
 * @param {import('./layout.js').TableLayout} layout
 */
export function renderUpCard(ctx, state, layout, animation = {}) {
  if (!state.up_card) return;

  const { x, y, w, h } = computeUpCardRect(layout);

  if (state.up_card_turned_down) {
    drawRejected(ctx, state.up_card, x, y, w, h);
    return;
  }

  // T9 hand-start spotlight: for the first second of bid_r1 the card
  // springs in (0.72 → 1.0) under a fading gold glow, matching the
  // server's HAND_SPOTLIGHT_PAD_MS so no bot bids on a card the human
  // hasn't visually registered yet. animation.intro = { t: 0..1 }.
  const intro = animation.intro;
  if (intro && intro.t < 1) {
    const t = Math.max(0, Math.min(1, intro.t));
    const scale = introScale(t);
    const cx = x + w / 2;
    const cy = y + h / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    renderCard(ctx, state.up_card, -w / 2, -h / 2, w, h, { face_up: true, trump: isTrumpCard(state.up_card, state.trump_suit) });
    ctx.restore();
    // Fading gold spotlight ring on top of the scaled card.
    const ringAlpha = 0.9 * (1 - t);
    if (ringAlpha > 0.02) {
      const rw = w * scale;
      const rh = h * scale;
      ctx.save();
      ctx.globalAlpha = ringAlpha;
      ctx.strokeStyle = COLOR_PICKUP_GLOW;
      ctx.lineWidth = 3;
      ctx.shadowColor = COLOR_PICKUP_GLOW;
      ctx.shadowBlur = 18;
      ctx.strokeRect(cx - rw / 2 - 3, cy - rh / 2 - 3, rw + 6, rh + 6);
      ctx.restore();
    }
    return;
  }
  renderCard(ctx, state.up_card, x, y, w, h, { face_up: true, trump: isTrumpCard(state.up_card, state.trump_suit) });
}

/**
 * Pure intro scale: 0.72 at t=0 springing to 1.0 at t=1 (overshoot
 * mid-curve per springOvershoot). Exported via _internals for tests.
 *
 * @param {number} t 0..1
 * @returns {number}
 */
function introScale(t) {
  return 0.72 + 0.28 * springOvershoot(Math.max(0, Math.min(1, t)));
}

/**
 * B1-8: caption for the dealer-pickup card. Names the dealer through
 * the view's seat labels ("Bot Atom picks up ♦", "You pick up ♦")
 * instead of the generic "Bot N" fallback, so a named human dealer is
 * never captioned as a bot. Pure, tested in upcard.test.js.
 *
 * @param {{
 *   up_card?: string|null,
 *   dealer_seat?: number|null,
 *   seat_occupants?: Record<string|number, {display_name?: string, is_bot?: boolean}>|null,
 * }} state
 * @param {1|2|3|4|null} mySeatLua  Lua-numbered (1..4) human seat, or null
 * @param {Record<number|string, string>|null|undefined} seatLabels
 * @returns {string}
 */
export function dealerPickupCaption(state, mySeatLua, seatLabels) {
  const dealer = state.dealer_seat ?? null;
  const fromLabels = seatLabels && dealer !== null
    ? (seatLabels[dealer] ?? seatLabels[String(dealer)])
    : undefined;
  // Without labels the viewer-as-dealer still reads as "You": the
  // occupants map only names other humans, never self.
  const isMyPickup = mySeatLua != null && dealer === mySeatLua;
  const dealerLabel = fromLabels ?? (isMyPickup ? 'You' : dealerLabelFor(state));
  const symbol = SUIT_SYMBOL[suitOf(state.up_card ?? '')] || '';
  if (dealerLabel === 'You') return `You pick up ${symbol}`;
  return `${dealerLabel} picks up ${symbol}`;
}

// B3c r2: the caption's line box. canvasFont(14) snaps to 16px type;
// Nunito's ink for "picks up ♥" (descenders included) stays inside 20px.
export const PICKUP_CAPTION_H = 20;
// The felt starts 10px in from the canvas edge (table.js FELT_INSET);
// the caption keeps 2px more so it never sits on the rim.
const PICKUP_FELT_EDGE = 12;

let captionMeasureCtx = null;

/**
 * B3c r2: the caption's width at the canvas type (canvasFont(14)), from
 * an offscreen canvas, for callers outside the render pass (the view
 * gives the trump splash the caption box). Browser only.
 *
 * @param {string} text
 * @returns {number}
 */
export function pickupCaptionWidth(text) {
  if (!captionMeasureCtx) {
    captionMeasureCtx = document.createElement('canvas').getContext('2d');
    if (!captionMeasureCtx) throw new Error('pickupCaptionWidth: no 2d canvas context');
  }
  captionMeasureCtx.font = canvasFont(14);
  return captionMeasureCtx.measureText(text).width;
}

/**
 * B3c r2: every table object the pickup caption must stay clear of, with
 * the gap each one needs: 8px from the hand and from the up-card (its
 * amber outline is drawn 4px outside the card), 4px from the timer ring,
 * the plates, the opponent fans, the scoreboard, the trump badge with
 * its label and the viewer's Discard prompt.
 *
 * @param {import('./layout.js').TableLayout} layout
 * @param {{prompt?: {x:number,y:number,w:number,h:number}|null,
 *   badge?: {x:number,y:number,w:number,h:number}|null}} [extra]
 * @returns {Array<{r:{x:number,y:number,w:number,h:number}, gap:number}>}
 */
export function pickupCaptionObstacles(layout, { prompt = null, badge = null } = {}) {
  const seats = layout.seats || [];
  const list = [
    ...(seats[0] ? [{ r: seats[0].handArea, gap: 8 }] : []),
    { r: computeUpCardRect(layout), gap: 8 },
    ...(layout.timerRing ? [{ r: layout.timerRing, gap: 4 }] : []),
    ...seats.map((s) => ({ r: s, gap: 4 })),
    ...seats.slice(1).map((s) => ({ r: s.handArea, gap: 4 })),
    ...(layout.scoreboard ? [{ r: layout.scoreboard, gap: 4 }] : []),
    ...(badge ? [{ r: badge, gap: 4 }] : []),
    ...(prompt ? [{ r: prompt, gap: 4 }] : []),
  ];
  return list;
}

/**
 * Caption's box in canvas coordinates. B3c r2: the first clear spot, in
 * order: centred under the up-card, left of it (bottom, middle, top),
 * right of it (same), centred above it, then the obstacles' free edges
 * nearest the card. A spot is clear when it is on
 * the felt and keeps each obstacle's gap (pickupCaptionObstacles).
 * `clear: false` means no spot was free; the box is then the one under
 * the card and the caller reports it. A legacy layout without seats
 * (old unit callers) has no obstacles and gets the spot under the card.
 *
 * @param {import('./layout.js').TableLayout} layout
 * @param {number} textWidth
 * @param {{prompt?: {x:number,y:number,w:number,h:number}|null,
 *   badge?: {x:number,y:number,w:number,h:number}|null}} [extra]
 * @returns {{x:number, y:number, w:number, h:number, clear:boolean}}
 */
export function dealerPickupCaptionRect(layout, textWidth, extra = {}) {
  const { x, y, w, h } = computeUpCardRect(layout);
  const tw = Math.ceil(textWidth);
  const th = PICKUP_CAPTION_H;
  const under = { x: Math.round(x + (w - tw) / 2), y: y + h + 8, w: tw, h: th, clear: true };
  if (!layout.seats || !layout.canvasW || !layout.canvasH) return under;
  const leftX = x - 8 - tw;
  const rightX = x + w + 8;
  const midY = Math.round(y + (h - th) / 2);
  const spots = [
    under,
    { x: leftX, y: y + h - th }, { x: leftX, y: midY }, { x: leftX, y },
    { x: rightX, y: y + h - th }, { x: rightX, y: midY }, { x: rightX, y },
    { x: under.x, y: y - 8 - th },
  ];
  const obstacles = pickupCaptionObstacles(layout, extra);
  const onFelt = (r) => r.x >= PICKUP_FELT_EDGE && r.y >= PICKUP_FELT_EDGE
    && r.x + r.w <= layout.canvasW - PICKUP_FELT_EDGE
    && r.y + r.h <= layout.canvasH - PICKUP_FELT_EDGE;
  const clearOf = (r, { r: o, gap }) => r.x + r.w + gap <= o.x || o.x + o.w + gap <= r.x
    || r.y + r.h + gap <= o.y || o.y + o.h + gap <= r.y;
  // Then every pairing of the obstacles' free edges, nearest the card
  // first (with the viewer's prompt on a short landscape the only room
  // is a strip above the prompt or beside the top fans).
  const xs = new Set([leftX, rightX, under.x, PICKUP_FELT_EDGE]);
  const ys = new Set([y, midY, y + h - th, under.y, y - 8 - th]);
  for (const { r: o, gap } of obstacles) {
    xs.add(o.x + o.w + gap);
    xs.add(o.x - gap - tw);
    ys.add(o.y + o.h + gap);
    ys.add(o.y - gap - th);
  }
  const cx = x + w / 2;
  const cy = y + h / 2;
  const grid = [];
  for (const gx of xs) {
    for (const gy of ys) grid.push({ x: gx, y: gy, d: Math.hypot(gx + tw / 2 - cx, gy + th / 2 - cy) });
  }
  grid.sort((a, b) => a.d - b.d || a.y - b.y || a.x - b.x);
  for (const spot of [...spots, ...grid]) {
    const r = { x: spot.x, y: spot.y, w: tw, h: th };
    if (onFelt(r) && obstacles.every((o) => clearOf(r, o))) return { ...r, clear: true };
  }
  return { ...under, clear: false };
}

/**
 * Render the up-card during the dealer_discard state with an amber
 * outline and a caption naming the dealer ("Bot Atom picks up ♦",
 * "You pick up ♦") so the table says which card is joining whose
 * hand.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   up_card?: string|null,
 *   dealer_seat?: number|null,
 *   trump_suit?: string|null,
 *   seat_occupants?: Record<string|number, {display_name?: string, is_bot?: boolean}>|null,
 *   maker_seat?: number|null,
 * }} state
 * @param {import('./layout.js').TableLayout} layout
 * @param {1|2|3|4|null} mySeatLua  Lua-numbered (1..4) human seat, or null
 * @param {Record<number|string, string>|null|undefined} [seatLabels]
 * @param {{prompt?: {x:number,y:number,w:number,h:number}|null}} [extra]
 *   the viewer's Discard prompt rect, when it is on screen
 * @returns {{x:number, y:number, w:number, h:number, clear:boolean}|undefined}
 *   the caption box (undefined when there is no up-card)
 */
export function renderDealerPickup(ctx, state, layout, mySeatLua, seatLabels, { prompt = null } = {}) {
  if (!state.up_card) return;
  const { x, y, w, h } = computeUpCardRect(layout);

  renderCard(ctx, state.up_card, x, y, w, h, { face_up: true, trump: isTrumpCard(state.up_card, state.trump_suit) });

  // Amber outline ring, visible signal that this card is in transit.
  ctx.save();
  ctx.strokeStyle = COLOR_PICKUP_GLOW;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.rect(x - 2, y - 2, w + 4, h + 4);
  ctx.stroke();
  ctx.restore();

  const caption = dealerPickupCaption(state, mySeatLua, seatLabels);

  ctx.save();
  ctx.font = canvasFont(14);
  ctx.fillStyle = COLOR_PICKUP_TEXT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const metrics = ctx.measureText(caption);
  const badge = layout.seats ? trumpBadgeStackRect(layout, state) : null;
  const box = dealerPickupCaptionRect(layout, metrics.width, { prompt, badge });
  if (!box.clear) {
    console.error('renderDealerPickup: no clear spot for the pickup caption; drawn under the up-card', {
      caption, canvasW: layout.canvasW, canvasH: layout.canvasH,
    });
  }
  const drawX = box.x;
  const drawY = box.y;
  ctx.fillText(caption, drawX, drawY);
  ctx.restore();
  // B3c r2 e2e seam: the ink box of the caption just drawn, from the
  // same coordinates fillText used, so a browser test checks the real
  // drawing rather than recomputing it.
  const el = ctx.canvas;
  if (el && el.dataset) {
    const left = Math.floor(drawX - (metrics.actualBoundingBoxLeft ?? 0));
    const right = Math.ceil(drawX + (metrics.actualBoundingBoxRight ?? metrics.width));
    const top = Math.floor(drawY - (metrics.actualBoundingBoxAscent ?? 0));
    const bottom = Math.ceil(drawY + (metrics.actualBoundingBoxDescent ?? PICKUP_CAPTION_H));
    el.dataset.pickupCaption = `${left},${top},${right - left},${bottom - top}`;
  }
  return box;
}

function drawRejected(ctx, cardId, x, y, w, h) {
  const suit = suitOf(cardId);
  if (!suit) return;
  // Ghosted card outline (no body fill, show the felt through it).
  ctx.save();
  ctx.strokeStyle = COLOR_REJECTED;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.stroke();
  // Faded suit pip in the middle.
  ctx.fillStyle = COLOR_REJECTED;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const pipSize = Math.max(28, Math.round(h * 0.45));
  ctx.font = canvasFont(pipSize);
  ctx.fillText(SUIT_SYMBOL[suit], x + w / 2, y + h / 2);
  // Strikethrough, diagonal line through the pip.
  ctx.strokeStyle = COLOR_STRIKE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(x + 8,     y + h - 8);
  ctx.lineTo(x + w - 8, y + 8);
  ctx.stroke();
  ctx.restore();
}

export const _internals = {
  introScale,
  UPCARD_W_DESKTOP,
  UPCARD_W_MOBILE,
  UPCARD_ASPECT,
  describeCardLong,
  dealerLabelFor,
  dealerPickupCaption,
  RANK_NAME,
};
